import axios from "axios";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Briefcase, MapPin, Sparkles, ChevronRight } from "lucide-react";
import { Helmet } from "react-helmet-async";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicJobsListPage = ({ orgMode = false }) => {
  const { slug } = useParams();
  const basePath = orgMode ? "orgs" : "companies";
  const jobLinkBase = orgMode ? `/c/org/${slug}` : `/c/${slug}`;
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [c, j] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/public/${basePath}/${slug}`),
          axios.get(`${BACKEND_URL}/api/public/${basePath}/${slug}/jobs`),
        ]);
        setCompany(c.data);
        setJobs(j.data || []);
      } catch (e) {
        setError(
          e?.response?.status === 404
            ? "Empresa não encontrada."
            : "Não foi possível carregar as vagas."
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, basePath]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-8 w-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div
          data-testid="public-error"
          className="bg-white border border-slate-200 rounded-lg p-8 max-w-md text-center"
        >
          <h1 className="heading text-xl font-semibold text-slate-900 mb-2">
            {error}
          </h1>
          <p className="text-sm text-slate-600">
            Verifique o link de carreiras ou peça um novo à empresa.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="public-jobs-list-page" className="min-h-screen bg-slate-50">
      {company && (
        <Helmet>
          <title>{`Vagas em ${company.company_name} · GridRecruiter`}</title>
          <meta
            name="description"
            content={`Confira as ${jobs.length} oportunidade${jobs.length !== 1 ? "s" : ""} abertas na ${company.company_name}. Candidate-se com análise por IA.`}
          />
          <meta property="og:title" content={`Vagas em ${company.company_name}`} />
          <meta
            property="og:description"
            content={`${jobs.length} oportunidade${jobs.length !== 1 ? "s" : ""} aberta${jobs.length !== 1 ? "s" : ""} agora. Envie seu currículo e receba análise automática.`}
          />
          <meta property="og:type" content="website" />
          {company.company_cover_url && (
            <meta
              property="og:image"
              content={`${typeof window !== "undefined" ? window.location.origin : ""}${company.company_cover_url}`}
            />
          )}
          {!company.company_cover_url && company.company_logo_url && (
            <meta
              property="og:image"
              content={`${typeof window !== "undefined" ? window.location.origin : ""}${company.company_logo_url}`}
            />
          )}
          <meta name="twitter:card" content={company.company_cover_url ? "summary_large_image" : "summary"} />
        </Helmet>
      )}
      {/* Hero */}
      <header
        className={`relative overflow-hidden text-white ${
          company?.company_cover_url
            ? ""
            : "bg-gradient-to-br from-blue-950 via-slate-950 to-emerald-950"
        }`}
      >
        {company?.company_cover_url ? (
          <>
            <img
              src={company.company_cover_url}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="public-hero-cover"
            />
            {/* Overlay gradient for text legibility — bottom/left heavy */}
            <div
              className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/75 to-slate-950/45"
              aria-hidden="true"
            />
          </>
        ) : (
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(29,112,214,0.4), transparent 60%), radial-gradient(circle at 80% 70%, rgba(16,185,129,0.35), transparent 60%)",
            }}
          />
        )}
        <div className="relative max-w-5xl mx-auto px-6 py-16 sm:py-24">
          {company?.company_logo_url && (
            <div className="mb-8 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/95 backdrop-blur p-3 shadow-2xl ring-1 ring-white/20">
              <img
                src={company.company_logo_url}
                alt={`Logo ${company.company_name}`}
                className="w-full h-full object-contain"
              />
            </div>
          )}
          <div className="inline-flex items-center gap-2 mb-4 bg-white/10 backdrop-blur px-3 py-1 rounded-full text-xs font-medium border border-white/15">
            <Sparkles className="w-3.5 h-3.5" />
            Carreiras
          </div>
          <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
            Junte-se ao time da{" "}
            <span className="bg-gradient-to-r from-blue-300 to-emerald-300 bg-clip-text text-transparent">
              {company?.company_name}
            </span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 mt-5 max-w-2xl leading-relaxed">
            Confira as oportunidades abertas. Cada candidatura é analisada com IA
            para garantir resposta rápida e justa.
          </p>
        </div>
      </header>

      {/* Jobs */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">
              Vagas abertas
            </div>
            <h2 className="heading text-2xl font-bold text-slate-950">
              {jobs.length} oportunidade{jobs.length !== 1 ? "s" : ""}
            </h2>
          </div>
        </div>

        {jobs.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-5 h-5 text-slate-600" />
            </div>
            <h3 className="heading text-lg font-semibold text-slate-900 mb-1">
              Nenhuma vaga aberta no momento
            </h3>
            <p className="text-sm text-slate-600">
              Volte em breve. Novas oportunidades surgem regularmente.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {jobs.map((j) => (
              <li key={j.id}>
                <Link
                  to={`${jobLinkBase}/${j.slug}`}
                  data-testid={`public-job-${j.slug}`}
                  className="group block bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-400 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <h3 className="heading text-lg font-semibold text-slate-900 leading-snug mb-1 group-hover:text-slate-950">
                        {j.title}
                      </h3>
                      {j.department && (
                        <div className="text-sm text-slate-600 mb-2">{j.department}</div>
                      )}
                      <p className="text-sm text-slate-600 line-clamp-2 mb-3 leading-relaxed">
                        {j.description}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-500">
                        {j.location && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {j.location}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Briefcase className="w-3.5 h-3.5" />
                          {j.employment_type}
                        </span>
                        {j.salary_range && <span>{j.salary_range}</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white mt-16">
        <div className="max-w-5xl mx-auto px-6 py-6 text-xs text-slate-500 text-center">
          Página de carreiras gerada por <strong className="text-brand-blue">GridRecruiter</strong>
        </div>
      </footer>
    </div>
  );
};

export default PublicJobsListPage;
